import React, { useState, useEffect, useRef } from "react";
import SectionDivider from "./SectionDivider";
import useSubjectAPI from "./useSubjectAPI";
import ProvidersSubsection from "./ProvidersSubsection";
import MedicalHistorySubsection from "./MedicalHistorySubsection";
import FamilyHistorySubsection from "./FamilyHistorySubsection";
import AllergiesSubsection from "./AllergiesSubsection";
import MedicationSubsection from "./MedicationSubsection";

const purple = "#2E2868";

const MedicalSection = ({ subjectId, form }) => {
  const {
    getAllergies,
    getConditions,
    getMedications,
    getProviderTypes,
    loadSubject,
    saveSubject,
  } = useSubjectAPI(subjectId);
  const isCreate = !subjectId;

  /* TOGGLE SECTIONS */
  const [subOpen, setSubOpen] = useState({
    providers: true,
    medicalHistory: true,
    familyHistory: true,
    allergies: true,
    medication: true,
  });

  const toggleSub = (key) =>
    setSubOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const [open, setOpen] = useState(true);
  const toggleMain = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!next) {
        setSubOpen({
          providers: false,
          medicalHistory: false,
          familyHistory: false,
          allergies: false,
          medication: false,
        });
      }
      return next;
    });
  };

  const [providers, setProviders] = useState([]);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [familyHistory, setFamilyHistory] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [medication, setMedication] = useState([]);
  const lastValidRef = useRef(null);

  // Load Initial Subject Data

useEffect(() => {
  if (!subjectId) return;

  let isMounted = true; 

  loadSubject().then((data) => {
    if (!data || !isMounted) return;
    
    lastValidRef.current = data;
    
    if (data.subject_physicians) {
      setProviders(data.subject_physicians.map(p => ({
        id: p.id,
        name: p.name ?? "",
        provider_type: p.provider_type ?? "",
      })));
    }

    if (data.subject_medical_history) {
      setMedicalHistory(data.subject_medical_history.map(m => ({
        id: m.id,
        condition_id: m.condition_id ?? null,
        condition_name: m.condition_name || m.condition_id_name || (m.condition && m.condition.name) || "",
        condition_status: m.condition_status ?? "",
        notes: m.notes ?? "",
      })));
    }
    if (data.subject_family_medical_history) {
      setFamilyHistory(
        data.subject_family_medical_history.map((f) => ({
          id: f.id,
          family_member: f.family_member ?? "",
          condition_id: f.condition_id ?? null,
          condition_name:
            f.condition_name ||
            f.condition_id_name ||
            (f.condition && f.condition.name) ||
            "",
          notes: f.notes ?? "",
        }))
      );
    }

    if (data.subject_allergies) {
      setAllergies(data.subject_allergies.map(a => ({
        id: a.id,
        allergy_name: a.allergy_name || a.allergy_id_name || (a.allergy && a.allergy.name) || "",
        severity: a.severity ?? "",
        reaction: a.reaction ?? "",
        notes: a.notes ?? "",
      })));
    }

    if (data.subject_medications) {
      setMedication(data.subject_medications.map(m => ({
        id: m.id,
        medication_id: m.medication_id ?? null,
        medication_name: m.medication_name || m.medication_id_name || (m.medication && m.medication.name) || "",
        dosage_and_frequency: m.dosage_and_frequency ?? "",
        notes: m.notes ?? "",
      })));
    }
  });

  return () => { isMounted = false; }; 
}, [subjectId]);
  /* RENDER */
  return (
    <div className="my-3" style={{ maxWidth: "490px" }}>
      <div className="p-3">
        {/* Main Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <span style={{ fontSize: "30px", color: "#b2a9ff", marginRight: "6px" }}>
              ⋮⋮
            </span>
            <h5
              className="fw-bold m-0"
              style={{ color: purple, cursor: "pointer" }}
              onClick={() => setOpen((prev) => !prev)}
            >
              Medical
            </h5>
          </div>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
            onClick={toggleMain}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: "14px" }} />
          </div>
        </div>

        {/* Subsections */}
        {open && (
          <>
            <ProvidersSubsection
              subjectId={subjectId}
              isCreate={isCreate}
              form={form}
              providers={providers}
              setProviders={setProviders}
              isOpen={subOpen.providers}
              onToggle={() => toggleSub("providers")}
              saveSubject={saveSubject}
              loadSubject={loadSubject}
              getProviderTypes={getProviderTypes}
            />

            <SectionDivider width="380px" />

            <MedicalHistorySubsection
              subjectId={subjectId}
              isCreate={isCreate}
              form={form}
              medicalHistory={medicalHistory}
              setMedicalHistory={setMedicalHistory}
              isOpen={subOpen.medicalHistory}
              onToggle={() => toggleSub("medicalHistory")}
              saveSubject={saveSubject}
              loadSubject={loadSubject}
              getConditions={getConditions}
            />

            <SectionDivider width="380px" />

            <FamilyHistorySubsection
              subjectId={subjectId}
              isCreate={isCreate}
              form={form}
              familyHistory={familyHistory}
              setFamilyHistory={setFamilyHistory}
              isOpen={subOpen.familyHistory}
              onToggle={() => toggleSub("familyHistory")}
              saveSubject={saveSubject}
              loadSubject={loadSubject}
              getConditions={getConditions}
            />

            <SectionDivider width="380px" />

            <AllergiesSubsection
              subjectId={subjectId}
              isCreate={isCreate}
              form={form}
              allergies={allergies}
              setAllergies={setAllergies}
              isOpen={subOpen.allergies}
              onToggle={() => toggleSub("allergies")}
              saveSubject={saveSubject}
              loadSubject={loadSubject}
              getAllergies={getAllergies}
            />

            <SectionDivider width="380px" />

            <MedicationSubsection
              subjectId={subjectId}
              isCreate={isCreate}
              form={form}
              medication={medication}
              setMedication={setMedication}
              isOpen={subOpen.medication}
              onToggle={() => toggleSub("medication")}
              saveSubject={saveSubject}
              loadSubject={loadSubject}
              getMedications={getMedications}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MedicalSection;